import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { EntryNoteService } from 'src/app/services/entry-note.service';
import { CoasService } from 'src/app/services/coas.service';
import { AssociationEntryNoteCoaService } from 'src/app/services/association-entry-note-coa.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { getStringDate } from 'src/app/common/util';
import { CoaResponse } from 'src/app/models/coa-parameter';
import { switchMap } from 'rxjs/operators';

type EntryNoteVM = {
  id: number;
  number: string;
  providerName: string;
  date: string;
};

@Component({
  selector: 'app-association-entry-note-coa',
  templateUrl: './association-entry-note-coa.component.html',
  styleUrls: ['./association-entry-note-coa.component.scss']
})
export class AssociationEntryNoteCoaComponent {

  loading = false;

  allEntryNotesNotAssociated: EntryNoteVM[] = [];
  allEntryNotesAssociated: EntryNoteVM[] = [];

  entryNotesNotAssociated: EntryNoteVM[] = [];
  entryNotesAssociated: EntryNoteVM[] = [];

  allCoas: CoaResponse[] = [];
  coas: CoaResponse[] = [];

  filterEntryNotes = '';
  filterCoas = '';

  selectedEntryNoteId: number | null = null;
  selectedEntryNote: EntryNoteVM | null = null;

  initialAssociatedCoaIds: number[] = [];
  currentAssociatedCoaIds: number[] = [];
  currentAssociatedCoas: CoaResponse[] = [];

  startDate = '';
  endDate = '';

  paginationNotAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationCoas = new SimplePaginationModel(0, 5, 0, 0, 0);

  coaDetails: any[] = [];
  loadingCoaDetails = false;

  entryNoteDetails: any[] = [];
  loadingEntryNoteDetails = false;


  @ViewChild('svgContainer', { static: true }) svgContainer!: ElementRef;
  @HostListener('window:resize') onResize() { this.drawLinks(); }

  private timeoutEntryNotes: any = null;
  private timeoutCoas: any = null;

  constructor(
    private toastService: ToastrService,
    private entryNoteService: EntryNoteService,
    private coasService: CoasService,
    private associationService: AssociationEntryNoteCoaService
  ) {
    const today = new Date();
    this.startDate = getStringDate(today);
    this.endDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.loadStartData();
  }

  loadStartData() {
    this.loadEntryNotesNotAssociated();
    this.loadEntryNotesAssociated();
    this.loadCoas();
  }

  loadEntryNotesNotAssociated() {
    this.entryNoteService.getEntryNotesAllNotAssociated().subscribe({
      next: (resp: ResponseObject) => {
        this.allEntryNotesNotAssociated = (resp.data || []).map((en: any) => this.normalizeEntryNote(en));
        this.refreshNotAssociatedPagination();
      },
      error: () => this.toastService.error('Error al obtener notas no asociadas')
    });
  }

  loadEntryNotesAssociated() {
    this.entryNoteService.getEntryNotesAllAssociated().subscribe({
      next: (resp: ResponseObject) => {
        this.allEntryNotesAssociated = (resp.data || []).map((en: any) => this.normalizeEntryNote(en));
        this.refreshAssociatedPagination();
      },
      error: () => this.toastService.error('Error al obtener notas asociadas')
    });
  }

  loadCoas() {
    this.coasService.getCoas().subscribe({
      next: (resp: ResponseObject) => {
        this.allCoas = resp.data || [];
        this.allCoas.sort((a, b) => Number(b.number) - Number(a.number));
        this.refreshCoasPagination();
      },
      error: () => this.toastService.error('Error al obtener COAs')
    });
  }

  private normalizeEntryNote(en: any): EntryNoteVM {
    return {
      id: en.id,
      number: String(en.noteNumber),
      providerName: en.providerData?.name ?? '—',
      date: new Date(en.entryDate).toLocaleDateString()
    };
  }

  selectEntryNote(entryNoteId: number) {
    if (this.selectedEntryNoteId === entryNoteId) return;

    this.selectedEntryNoteId = entryNoteId;
    this.selectedEntryNote =
      this.allEntryNotesNotAssociated.find(e => e.id === entryNoteId) ||
      this.allEntryNotesAssociated.find(e => e.id === entryNoteId) || null;

    this.currentAssociatedCoaIds = [];
    this.currentAssociatedCoas = [];
    this.coaDetails = []; // Limpia el detalle previo

    this.associationService.getAssociationsByEntryNote(entryNoteId).subscribe({
      next: (resp: ResponseObject) => {
        const associations = resp.data || [];
        const relatedIds = associations.map((a: any) => a.coaId);

        this.initialAssociatedCoaIds = [...relatedIds];
        this.currentAssociatedCoaIds = [...relatedIds];
        this.currentAssociatedCoas = this.allCoas.filter(c => relatedIds.includes(c.id));

        this.refreshCoasPagination();
        this.drawLinks();

        if (this.currentAssociatedCoaIds.length > 0) {
          const firstCoaId = this.currentAssociatedCoaIds[0];
          this.getCoasDetailByIdCoa(firstCoaId);
        }

        this.getEntryNoteDetails(entryNoteId);
      },
      error: () => this.toastService.error('Error al obtener asociaciones de la nota de entrada')
    });
  }

  getEntryNoteDetails(entryNoteId: number) {
    this.loadingEntryNoteDetails = true;
    this.entryNoteService.getEntryNoteById(entryNoteId).subscribe({
      next: (resp: ResponseObject) => {
        this.entryNoteDetails = resp.data?.details || [];
        this.loadingEntryNoteDetails = false;
      },
      error: () => {
        this.toastService.error('Error al obtener detalles de la nota de entrada');
        this.loadingEntryNoteDetails = false;
      }
    });
  }



  toggleCoa(coa: CoaResponse) {
    if (!this.selectedEntryNoteId) {
      this.toastService.warning('Selecciona primero una nota de entrada.');
      return;
    }

    const isSelected = this.currentAssociatedCoaIds.includes(coa.id);
    if (!isSelected && this.currentAssociatedCoaIds.length >= 1) {
      this.toastService.warning('Solo se permite asociar un COA por nota de entrada.');
      return;
    }

    this.currentAssociatedCoaIds = isSelected ? [] : [coa.id];
    this.currentAssociatedCoas = this.allCoas.filter(c => this.currentAssociatedCoaIds.includes(c.id));
    this.refreshCoasPagination();
    this.drawLinks();

    if (!isSelected) {
      this.getCoasDetailByIdCoa(coa.id);
    } else {
      this.coaDetails = [];
    }
  }


  getDisabledSave(): boolean {
    const initialSet = new Set(this.initialAssociatedCoaIds);
    const currentSet = new Set(this.currentAssociatedCoaIds);
    const toAdd = this.currentAssociatedCoaIds.some(id => !initialSet.has(id));
    const toRemove = this.initialAssociatedCoaIds.some(id => !currentSet.has(id));
    return !(toAdd || toRemove) || !this.selectedEntryNoteId || this.loading;
  }

  saveAssociations() {
    if (!this.selectedEntryNoteId) return;
    const entryNoteId = this.selectedEntryNoteId;
    const initialSet = new Set(this.initialAssociatedCoaIds);
    const currentSet = new Set(this.currentAssociatedCoaIds);
    const toAdd = this.currentAssociatedCoaIds.filter(id => !initialSet.has(id));
    const toRemove = this.initialAssociatedCoaIds.filter(id => !currentSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.toastService.info('No hay cambios por guardar.');
      return;
    }

    this.loading = true;
    const deleteOps = toRemove.map(coaId => this.associationService.deleteAssociation(0, entryNoteId, coaId));
    const createOps = toAdd.map(coaId => this.associationService.createAssociation(entryNoteId, coaId));
    const deleteStep = deleteOps.length > 0 ? forkJoin(deleteOps) : of(null);

    deleteStep.pipe(
      switchMap(() => createOps.length > 0 ? forkJoin(createOps) : of(null))
    ).subscribe({
      next: () => {
        this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
        this.loading = false;
        this.loadStartData();
        this.selectEntryNote(entryNoteId);
      },
      error: (e: any) => {
        console.error(e);
        this.toastService.error(e?.error?.message || 'Error al actualizar asociaciones', 'Error');
        this.loading = false;
      }
    });
  }

  refreshNotAssociatedPagination() {
    const q = this.filterEntryNotes.toLowerCase();
    const filtered = this.allEntryNotesNotAssociated.filter(e =>
      e.number.toLowerCase().includes(q) || e.providerName.toLowerCase().includes(q)
    );
    this.paginationNotAssociated.total = filtered.length;
    this.entryNotesNotAssociated = filtered.slice(
      this.paginationNotAssociated.page * this.paginationNotAssociated.size,
      (this.paginationNotAssociated.page + 1) * this.paginationNotAssociated.size
    );
  }

  refreshAssociatedPagination() {
    const q = this.filterEntryNotes.toLowerCase();
    const filtered = this.allEntryNotesAssociated.filter(e =>
      e.number.toLowerCase().includes(q) || e.providerName.toLowerCase().includes(q)
    );
    this.paginationAssociated.total = filtered.length;
    this.entryNotesAssociated = filtered.slice(
      this.paginationAssociated.page * this.paginationAssociated.size,
      (this.paginationAssociated.page + 1) * this.paginationAssociated.size
    );
  }

  refreshCoasPagination() {
    const q = (this.filterCoas || '').toLowerCase();
    const matches = (c: CoaResponse) =>
      (c.code || '').toLowerCase().includes(q) ||
      (c.number + '').toLowerCase().includes(q) ||
      (c.productData?.name || '').toLowerCase().includes(q);

    const associatedSet = new Set(this.currentAssociatedCoaIds);
    const pinned = this.allCoas.filter(c => associatedSet.has(c.id));
    const rest = this.allCoas.filter(c => !associatedSet.has(c.id) && matches(c));
    const final = [...pinned, ...rest];

    this.paginationCoas.total = final.length;
    this.coas = final.slice(0, this.paginationCoas.size);
  }

  drawLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = ''; // limpiar líneas previas

    // Si no hay nota seleccionada o no hay COAs asociados, no dibujar nada
    if (!this.selectedEntryNoteId || this.currentAssociatedCoaIds.length === 0) return;

    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();

      // Buscar en ambas tablas la fila seleccionada
      const selectedRow =
        document.querySelector('.table tr.selected') as HTMLElement; // cualquier tabla de entry notes
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();

      // Punto de partida (lado derecho de la fila de nota)
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      for (const id of this.currentAssociatedCoaIds) {
        const relatedRow = document.querySelector(`#table-coas tr[data-id="${id}"]`) as HTMLElement;
        if (!relatedRow) continue;

        const rect = relatedRow.getBoundingClientRect();
        const x2 = rect.left - parentRect.left - 12;
        const y2 = rect.top + rect.height / 2 - parentRect.top;

        const xMiddle = x1 + (x2 - x1) / 2;

        this.drawLine(svg, x1, xMiddle, y1, y1);
        this.drawLine(svg, xMiddle, x2, y2, y2);
        this.drawLine(svg, xMiddle, xMiddle, y1, y2);
      }
    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }

  debouncedFilterCoas() {
    clearTimeout(this.timeoutCoas);
    this.timeoutCoas = setTimeout(() => {
      this.paginationCoas.page = 0;
      this.refreshCoasPagination();
    }, 250);
  }

  getCoasDetailByIdCoa(coaId: number) {
    this.loadingCoaDetails = true;
    this.coasService.getCoasDetailById(coaId).subscribe({
      next: (resp: ResponseObject) => {
        this.coaDetails = resp.data || [];
        this.loadingCoaDetails = false;
      },
      error: () => {
        this.toastService.error('Error al obtener el detalle del COA');
        this.loadingCoaDetails = false;
      }
    });
  }


}
